import { Card } from "@vb/ui";
import { Seo } from "../lib/seo.js";
import { Section, SectionHeading } from "../components/Section.js";
import { LICENSING_STANCE } from "../content/jurisdictions.js";

const MANIFESTO = [
  "We won't ship a mobile-slot-lobby UI. Numbers are the hero.",
  "We won't custody player funds. Operator wallets are the source of truth.",
  "We won't ship adaptive-difficulty math. Edges are fixed, published, and verifiable every round.",
  "No third-party SaaS in our lead-handling path. Every contact, booking, and notification lives in our own server. Operators get fewer eyes on their pipeline.",
];

export function About() {
  return (
    <>
      <Seo
        title="About · Vault Breaker Studios"
        description="Vault Breaker Studios — a new B2B crypto casino game studio."
        path="/about"
      />

      <Section>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(44px, 6vw, 72px)",
            lineHeight: 1,
            margin: 0,
            color: "var(--fg-1)",
            textTransform: "uppercase",
          }}
        >
          We're new. On purpose.
        </h1>
        <div style={{ display: "grid", gap: 16, maxWidth: 780, marginTop: 20 }}>
          <p style={{ fontSize: 17, color: "var(--fg-2)", margin: 0, lineHeight: 1.6 }}>
            Vault Breaker Studios is a pre-launch B2B game studio. We aren't pretending to have
            operators on the books or rounds in production. We have one game — Vault Breaker — that
            we think is good, and we'd like a first operator to put it in front of players.
          </p>
          <p style={{ fontSize: 17, color: "var(--fg-2)", margin: 0, lineHeight: 1.6 }}>
            Our deal is simple: the game is free to integrate. We take 10% of the gross gaming
            revenue it earns you — no setup fees, no monthly minimums, no licence fees. If the game
            doesn't earn, neither do we. Aligned incentives beat a retainer.
          </p>
          <p style={{ fontSize: 17, color: "var(--fg-2)", margin: 0, lineHeight: 1.6 }}>
            We're B2B only. We never accept player deposits. Operators integrate against their own
            wallets and their own regulatory surface. We ship the math, the client, and the RGS.
            You ship the brand.
          </p>
        </div>
      </Section>

      <Section>
        <SectionHeading eyebrow="Team" title="Who's behind this" />
        <Card style={{ display: "grid", gap: 14, padding: 28 }}>
          <p style={{ margin: 0, fontSize: 16, color: "var(--fg-1)", lineHeight: 1.6 }}>
            <strong>Federico</strong> — founder, sole engineer. Built the math engine, the
            game client, the RGS, and the operator dashboard end-to-end. Background in systems
            and product engineering. Treats this studio as a one-person shop until a paying
            operator says otherwise.
          </p>
          <p style={{ margin: 0, fontSize: 14, color: "var(--fg-2)", lineHeight: 1.6 }}>
            Vault Breaker exists because the established crash providers — Spribe, BGaming,
            Hacksaw — ship sound math but treat transparency as a checkbox. Seeds are
            revealed but never committed publicly. Theoretical RTP is published, actual RTP
            isn't. "Provably fair" stops at the marketing copy. The bet is that operators in
            second-tier jurisdictions (LatAm, regulated EU, Ontario) need a defensibility
            story their existing suppliers can't give them, and that radical transparency is
            the cheapest moat a small studio can build.
          </p>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--fg-3)", lineHeight: 1.6 }}>
            Want a video intro or a 30-min integration walkthrough? Drop a note via{" "}
            <a href="/contact" style={{ color: "var(--cyan)" }}>
              contact sales
            </a>
            .
          </p>
          <div
            style={{
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
              paddingTop: 14,
              borderTop: "1px solid var(--line)",
              marginTop: 8,
            }}
          >
            {/* TODO: confirm canonical org URL before launch */}
            <a
              href="https://github.com/vaultbreaker-studios/engine"
              target="_blank"
              rel="noreferrer noopener"
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 12,
                color: "var(--cyan)",
                textDecoration: "none",
                padding: "6px 12px",
                border: "1px solid var(--line)",
                borderRadius: 4,
              }}
            >
              GitHub ↗
            </a>
          </div>
        </Card>
      </Section>

      <Section>
        <SectionHeading eyebrow="Manifesto" title="What we won't do" />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          {MANIFESTO.map((m, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                gap: 16,
                alignItems: "flex-start",
                padding: "18px 22px",
                borderTop: i === 0 ? "none" : "1px solid var(--line)",
              }}
            >
              <span
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: 24,
                  color: "var(--gold-bright)",
                  lineHeight: 1,
                  minWidth: 28,
                }}
              >
                {String(i + 1).padStart(2, "0")}
              </span>
              <p style={{ margin: 0, fontSize: 15, color: "var(--fg-1)", lineHeight: 1.5 }}>{m}</p>
            </div>
          ))}
        </Card>
      </Section>

      <Section>
        <SectionHeading eyebrow="Licensing" title={LICENSING_STANCE.heading} />
        <Card style={{ display: "grid", gap: 14, padding: 28 }}>
          {LICENSING_STANCE.paragraphs.map((p, i) => (
            <p key={i} style={{ margin: 0, fontSize: 15, color: "var(--fg-2)", lineHeight: 1.6 }}>
              {p}
            </p>
          ))}
        </Card>
      </Section>
    </>
  );
}
